import clsx from "clsx";
import { FC, Ref, useEffect, useRef } from "react";

import { TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Listbox } from "mds/components/Listbox";
import {
  ClickEvent,
  MenuDirection,
  MenuInstance,
  MenuItem,
  MenuType,
  SubMenu,
} from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CalendarIcon, ChevronDownIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectSortedTerms, selectSortedTermsForCurrentUser } from "store/selectors";
import { trackEvent } from "utils/amplitude";

export type TermMenuProps = {
  selectedTermId: string | null;
  onTermChange: (termId: string) => void;
  menuType?: MenuType;
  showIcons?: boolean;
  size?: "xs" | "s";
  defaultToAll?: boolean;
  showEmptyOption?: boolean;
  eventPrefix?: string;
  filterToCurrentUser?: boolean;
  direction?: MenuDirection;
  hideIfNoTerms?: boolean;
  className?: string;
};

export const TermMenuText = ({ text, showIcons }: { text: string; showIcons?: boolean }) =>
  showIcons ? <IconText iconStart={<CalendarIcon />} text={text} /> : text;

export const TermMenu: FC<TermMenuProps> = ({
  selectedTermId,
  onTermChange,
  menuType = "submenu",
  showIcons = true,
  defaultToAll = true,
  showEmptyOption = true,
  size = "xs",
  eventPrefix = "",
  filterToCurrentUser = false,
  direction = "bottom",
  hideIfNoTerms = false,
  className,
}) => {
  const subMenuRef: Ref<MenuInstance> = useRef(null);
  const allTerms = useAppSelector(selectSortedTerms);
  const termsForCurrentUser = useAppSelector(selectSortedTermsForCurrentUser);
  const terms = filterToCurrentUser ? termsForCurrentUser : allTerms;
  const defaultText = defaultToAll ? t("import_modal.all_terms") : t("import_modal.select_term");
  const termOptions = showEmptyOption ? [{ id: "", title: defaultText }, ...terms] : terms;

  const selectedTerm = termOptions.find((term) => term.id === selectedTermId);

  useEffect(() => {
    storeApi.terms.list();
  }, []);

  if (hideIfNoTerms && terms.length === 0) {
    return null;
  }

  const listboxItems = termOptions.map((termOption) => ({
    id: termOption.id,
    title: termOption.title,
    iconStart: showIcons ? <CalendarIcon /> : undefined,
  }));

  if (menuType === "menu") {
    return (
      <Listbox
        buttonSize={size}
        className={className}
        items={listboxItems}
        selectedValue={selectedTermId ?? ""}
        onSelectionChange={(item) => {
          trackEvent(`${eventPrefix}Term selected`, {
            eventCategory: "Button press",
            term: item.title,
          });
          onTermChange(item.id);
        }}
      />
    );
  }

  const onItemClick = (e: ClickEvent) => {
    const newTerm = e.value as TermType;
    onTermChange(newTerm.id);
    // This is a supported feature of szhsin/react-menu which allows
    // the menu to stay open even if a menu item is clicked. We use this to keep parent menus open
    // eslint-disable-next-line no-param-reassign
    e.keepOpen = true;
    subMenuRef.current?.closeMenu();
  };

  const menuButton = (
    <SquareButton isSelected={false} kind="tertiary" size={size}>
      <TermMenuText showIcons={showIcons} text={selectedTerm?.title ?? ""} />
      <ChevronDownIcon />
    </SquareButton>
  );

  return (
    <SubMenu
      className="dropdown"
      direction={direction}
      instanceRef={subMenuRef}
      label={menuButton}
      openTrigger="clickOnly"
      position="initial"
    >
      {termOptions.map((termOption) => {
        const selected = selectedTerm?.id === termOption.id;
        return (
          <MenuItem
            checked={selected}
            className={clsx({ selected, "text-white": selected }, "center dropdown-item")}
            eventName={`${eventPrefix}Term selected`}
            eventProps={{ term: termOption.title }}
            key={termOption.id}
            value={termOption}
            onClick={onItemClick}
          >
            <TermMenuText showIcons={showIcons} text={termOption.title} />
          </MenuItem>
        );
      })}
    </SubMenu>
  );
};
